import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiEye,
  FiEdit,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiUsers,
  FiUserCheck,
  FiUserX,
  FiShield,
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import teamService from '../../services/teamService';
import { TeamMember } from '../../types/team.types';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Loader from '../../components/common/Loader/Loader';
import { formatToIST, getRelativeTime } from '../../utils/dateHelper';
import styles from './TeamList.module.css';

interface FilterState {
  search: string;
  role: string;
  isActive: string;
  sortBy: string;
  order: 'asc' | 'desc';
}

const TeamList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    role: '',
    isActive: '',
    sortBy: 'createdAt',
    order: 'desc',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    usersPerPage: 10,
  });

  useEffect(() => {
    fetchTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.role, filters.isActive, filters.sortBy, filters.order, pagination.currentPage]);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await teamService.getTeamMembers({
        page: pagination.currentPage,
        limit: 10,
        role: filters.role || undefined,
        isActive: filters.isActive ? filters.isActive === 'true' : undefined,
        search: filters.search || undefined,
        sortBy: filters.sortBy,
        order: filters.order,
      });

      setTeamMembers(response.data?.users || []);

      if (response.pagination) {
        setPagination({
          currentPage: response.pagination.currentPage,
          totalPages: response.pagination.totalPages,
          totalUsers: response.pagination.totalUsers || 0,
          usersPerPage: response.pagination.usersPerPage || 10,
        });
      }
    } catch (error: any) {
      console.error('Failed to fetch team members:', error);
      toast.error(error.response?.data?.message || 'Failed to load team members');
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchTeamMembers();
  };

  const handleDelete = async (id: string, name: string, userRole: string) => {
    if (userRole === 'superadmin') {
      toast.error('Cannot delete SuperAdmin account');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${name}"?\n\nThis action cannot be undone.`)) return;

    try {
      console.log('🗑️ Deleting team member with ID:', id);
      await teamService.deleteTeamMember(id);
      toast.success('Team member deleted successfully');
      fetchTeamMembers();
    } catch (error: any) {
      console.error('❌ Delete failed:', error);
      toast.error(error.response?.data?.message || 'Failed to delete team member');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      superadmin: '#DC2626',
      admin: '#F59E0B',
      developer: '#8B5CF6',
      editor: '#3B82F6',
      manager: '#10B981',
      executive: '#06B6D4',
      blogger: '#EC4899',
      affiliater: '#6366F1',
    };
    return colors[role] || '#64748b';
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      superadmin: 'Super Admin',
      admin: 'Admin',
      developer: 'Developer',
      editor: 'Editor',
      manager: 'Manager',
      executive: 'Executive',
      blogger: 'Blogger',
      affiliater: 'Affiliater',
    };
    return labels[role] || role;
  };

  return (
    <div className={styles.teamList}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <FiUsers className={styles.titleIcon} />
            Team Management
          </h1>
          <p className={styles.subtitle}>
            Manage team members and their roles • Total: <strong>{pagination.totalUsers}</strong> members
          </p>
        </div>
        <div className={styles.headerRight}>
          {user?.role === 'superadmin' && (
            <Button variant="primary" icon={<FiPlus />} onClick={() => navigate('/team/create')}>
              Add Team Member
            </Button>
          )}
        </div>
      </div>

      

      {/* Filters */}
      <Card className={styles.filtersCard}>
        <form onSubmit={handleSearch} className={styles.filtersForm}>
          <div className={styles.searchBox}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search by name, email, user ID, mobile..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className={styles.searchInput}
            />
            <Button type="submit" variant="primary" size="small">
              Search
            </Button>
          </div>

          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <FiFilter /> Role
              </label>
              <select
                value={filters.role}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                className={styles.filterSelect}
              >
                <option value="">All Roles</option>
                <option value="superadmin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="developer">Developer</option>
                <option value="editor">Editor</option>
                <option value="manager">Manager</option>
                <option value="executive">Executive</option>
                <option value="blogger">Blogger</option>
                <option value="affiliater">Affiliater</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Status</label>
              <select
                value={filters.isActive}
                onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
                className={styles.filterSelect}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Sort By</label>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                className={styles.filterSelect}
              >
                <option value="createdAt">Created Date</option>
                <option value="name">Name</option>
                <option value="email">Email</option>
                <option value="lastLogin">Last Login</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Order</label>
              <select
                value={filters.order}
                onChange={(e) => setFilters({ ...filters, order: e.target.value as 'asc' | 'desc' })}
                className={styles.filterSelect}
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </form>
      </Card>

      {/* Team Table */}
      {loading ? (
        <div className={styles.loaderContainer}>
          <Loader size="large" text="Loading team members..." />
        </div>
      ) : teamMembers.length === 0 ? (
        <Card className={styles.emptyState}>
          <FiUsers className={styles.emptyIcon} />
          <h3>No Team Members Found</h3>
          <p>
            {filters.search || filters.role || filters.isActive
              ? 'Try adjusting your filters'
              : 'Start by adding your first team member'}
          </p>
          {user?.role === 'superadmin' && (
            <Button variant="primary" icon={<FiPlus />} onClick={() => navigate('/team/create')}>
              Add Team Member
            </Button>
          )}
        </Card>
      ) : (
        <>
          <Card className={styles.tableCard}>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thProfile}>Profile</th>
                    <th className={styles.thUser}>User Details</th>
                    <th className={styles.thRole}>Role</th>
                    <th className={styles.thContact}>Contact</th>
                    <th className={styles.thStatus}>Status</th>
                    <th className={styles.thActivity}>Last Activity</th>
                    <th className={styles.thActions}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map((member) => (
                    <tr key={member.id} className={styles.tableRow}>
                      {/* Profile */}
                      <td className={styles.tdProfile}>
                        <img
                          src={
                            member.profilePhoto?.url ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=3B82F6&color=fff`
                          }
                          alt={member.name}
                          className={styles.profileImage}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=3B82F6&color=fff`;
                          }}
                        />
                      </td>

                      {/* User Details */}
                      <td className={styles.tdUser}>
                        <div className={styles.userCell}>
                          <p className={styles.userName}>{member.name}</p>
                          <p className={styles.userEmail}>{member.email}</p>
                          <p className={styles.userID}>ID: {member.userID}</p>
                        </div>
                      </td>

                      {/* Role */}
                      <td className={styles.tdRole}>
                        <span
                          className={styles.roleBadge}
                          style={{ backgroundColor: getRoleBadgeColor(member.role) }}
                        >
                          {getRoleLabel(member.role)}
                        </span>
                      </td>

                      {/* Contact */}
                      <td className={styles.tdContact}>
                        <div className={styles.contactCell}>
                          <p className={styles.mobile}>📱 {member.mobile}</p>
                          <div className={styles.verificationBadges}>
                            {member.isEmailVerified && (
                              <span className={styles.verifiedBadge}>✓ Email</span>
                            )}
                            {member.isMobileVerified && (
                              <span className={styles.verifiedBadge}>✓ Mobile</span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className={styles.tdStatus}>
                        <span className={`${styles.statusBadge} ${member.isActive ? styles.statusActive : styles.statusInactive}`}>
                          {member.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      {/* Last Activity */}
                      <td className={styles.tdActivity}>
                        <div className={styles.activityCell}>
                          {member.lastLogin ? (
                            <>
                              <p className={styles.activityDate}>
                                {formatToIST(member.lastLogin, 'dd MMM yyyy')}
                              </p>
                              <p className={styles.activityTime}>
                                {getRelativeTime(member.lastLogin)}
                              </p>
                            </>
                          ) : (
                            <p className={styles.noActivity}>Never logged in</p>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className={styles.tdActions}>
                        <div className={styles.actionsCell}>
                          <button
                            className={styles.actionBtn}
                            onClick={() => navigate(`/team/view/${member.id}`)}
                            title="View Details"
                          >
                            <FiEye />
                          </button>

                          {(user?.role === 'superadmin' || user?.role === 'admin') && (
                            <button
                              className={styles.actionBtn}
                              onClick={() => navigate(`/team/edit/${member.id}`)}
                              title="Edit Member"
                            >
                              <FiEdit />
                            </button>
                          )}

                          {user?.role === 'superadmin' && member.role !== 'superadmin' && (
                            <button
                              className={`${styles.actionBtn} ${styles.deleteBtn}`}
                              onClick={() => handleDelete(member.id, member.name, member.role)}
                              title="Delete Member"
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className={styles.pagination}>
              <Button
                variant="ghost"
                disabled={pagination.currentPage === 1}
                onClick={() =>
                  setPagination((prev) => ({ ...prev, currentPage: prev.currentPage - 1 }))
                }
              >
                Previous
              </Button>

              <div className={styles.pageNumbers}>
                {(() => {
                  const pages: number[] = [];
                  const maxVisible = 5;
                  let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisible / 2));
                  let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);

                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                  }

                  return pages.map((page) => (
                    <button
                      key={page}
                      className={`${styles.pageButton} ${
                        page === pagination.currentPage ? styles.activePage : ''
                      }`}
                      onClick={() => setPagination((prev) => ({ ...prev, currentPage: page }))}
                    >
                      {page}
                    </button>
                  ));
                })()}
              </div>

              <Button
                variant="ghost"
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() =>
                  setPagination((prev) => ({ ...prev, currentPage: prev.currentPage + 1 }))
                }
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TeamList;